import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Tenancy } from '../../lib/types';
import { useApiQuery, useCursorList } from '../../lib/useApi';
import { BackLink, EmptyState, ErrorNote, Field, LoadMoreButton, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function Tenancies() {
  const [state, setState] = useState('');
  const navigate = useNavigate();
  const basePath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '25');
    if (state) params.set('state', state);
    return `/api/v1/housing/tenancies?${params.toString()}`;
  }, [state]);
  const list = useCursorList<Tenancy>(basePath);

  return (
    <div>
      <PageHeader title="Tenancies" subtitle="Allocated units under a Tenancy: rent invoicing (SeyPay-backed) and maintenance dispatch." />

      <div className="card mb-4 flex flex-wrap items-end gap-3 p-4">
        <Field label="State" className="w-48">
          <select className="field-input" value={state} onChange={(e) => setState(e.target.value)}>
            <option value="">All states</option>
            <option value="ACTIVE">Active</option>
            <option value="ARREARS">Arrears</option>
            <option value="TERMINATED">Terminated</option>
          </select>
        </Field>
      </div>

      {list.error ? <div className="mb-4"><ErrorNote message={list.error} /></div> : null}

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[600px] border-collapse">
          <thead>
            <tr>
              <th className="table-head">Tenant NIN</th>
              <th className="table-head">Unit id</th>
              <th className="table-head">Rent</th>
              <th className="table-head">State</th>
              <th className="table-head">Start date</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((t) => (
              <tr key={t.id} className="table-row-link" onClick={() => navigate(`/housing/tenancies/${t.id}`)}>
                <td className="table-cell font-semibold text-slate-800">{t.tenantNin}</td>
                <td className="table-cell text-xs text-slate-500">{t.unitId}</td>
                <td className="table-cell">{scr(t.rentAmount)}</td>
                <td className="table-cell"><StatusBadge status={t.state} /></td>
                <td className="table-cell text-xs text-slate-500">{formatDate(t.startDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.loading ? <Spinner label="Loading tenancies…" /> : null}
        {!list.loading && list.items.length === 0 ? <EmptyState title="No tenancies found" hint="Tenancies are created from an ALLOCATED housing application." /> : null}
      </div>
      <LoadMoreButton nextCursor={list.nextCursor} loading={list.loading} onClick={list.loadMore} />
    </div>
  );
}

export function TenancyDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const isAdmin = persona?.roles.includes('TENANCY_ADMIN') ?? false;
  const tenancyQ = useApiQuery<Tenancy>(`/api/v1/housing/tenancies/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [period, setPeriod] = useState('');
  const [maintDescription, setMaintDescription] = useState('');
  const [maintPriority, setMaintPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'EMERGENCY'>('MEDIUM');

  const act = async (fn: () => Promise<unknown>, message?: string) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await fn();
      tenancyQ.reload();
      if (message) setNote(message);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (tenancyQ.loading) return <Spinner label="Loading tenancy…" />;
  if (tenancyQ.error || !tenancyQ.data) return <ErrorNote message={tenancyQ.error ?? 'Tenancy not found'} />;
  const t = tenancyQ.data;

  return (
    <div>
      <BackLink to="/housing/tenancies" />
      <PageHeader title={t.tenantNin} subtitle={`Unit ${t.unitId} · rent ${scr(t.rentAmount)}`} actions={<StatusBadge status={t.state} />} />
      {error ? <div className="mb-4"><ErrorNote message={error} /></div> : null}
      {note ? <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{note}</div> : null}

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="card p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">Rent invoices</h2>
            {isAdmin ? (
              <div className="flex items-center gap-2">
                <input className="field-input !w-32 !py-1" placeholder="YYYY-MM" value={period} onChange={(e) => setPeriod(e.target.value)} />
                <button
                  className="btn-secondary btn-xs"
                  disabled={busy}
                  onClick={() => act(() => api.post(`/api/v1/housing/tenancies/${id}/invoices/generate`, { period: period || undefined }), 'Invoice generated.')}
                >
                  Generate next
                </button>
              </div>
            ) : null}
          </div>
          <div className="space-y-2">
            {(t.invoices ?? []).map((inv) => (
              <div key={inv.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
                <div>
                  <div className="font-semibold text-slate-800">{inv.period}</div>
                  <div className="text-xs text-slate-500">Due {formatDate(inv.dueDate)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="tabular-nums">{scr(inv.amountDue)}</span>
                  <StatusBadge status={inv.status} />
                  {isAdmin && inv.status !== 'PAID' ? (
                    <button
                      className="btn-secondary btn-xs"
                      disabled={busy}
                      onClick={() => act(() => api.post(`/api/v1/housing/tenancies/${id}/invoices/${inv.id}/mark-paid`), 'Invoice marked paid.')}
                    >
                      Mark paid
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
            {(t.invoices ?? []).length === 0 ? <EmptyState title="No invoices yet" /> : null}
          </div>
        </div>

        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Maintenance requests</h2>
          <div className="mb-3 space-y-2">
            {(t.maintenanceRequests ?? []).map((m) => (
              <div key={m.id} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800">{m.description}</span>
                  <div className="flex items-center gap-1.5">
                    <StatusBadge status={m.priority} />
                    <StatusBadge status={m.status} />
                  </div>
                </div>
                {isAdmin && (m.status === 'OPEN' || m.status === 'DISPATCHED_M20') ? (
                  <button
                    className="btn-secondary btn-xs mt-2"
                    disabled={busy}
                    onClick={() => act(() => api.post(`/api/v1/housing/maintenance-requests/${m.id}/status`, { status: 'IN_PROGRESS' }), 'Marked in progress.')}
                  >
                    Mark in progress
                  </button>
                ) : null}
                {isAdmin && m.status === 'IN_PROGRESS' ? (
                  <button className="btn-success btn-xs mt-2" disabled={busy} onClick={() => act(() => api.post(`/api/v1/housing/maintenance-requests/${m.id}/status`, { status: 'RESOLVED' }), 'Marked resolved.')}>
                    Mark resolved
                  </button>
                ) : null}
              </div>
            ))}
            {(t.maintenanceRequests ?? []).length === 0 ? <EmptyState title="No maintenance requests" /> : null}
          </div>
          <div className="border-t border-slate-100 pt-3">
            <div className="mb-2 grid gap-2 sm:grid-cols-2">
              <Field label="Description" className="sm:col-span-2">
                <input className="field-input" value={maintDescription} onChange={(e) => setMaintDescription(e.target.value)} />
              </Field>
              <Field label="Priority">
                <select className="field-input" value={maintPriority} onChange={(e) => setMaintPriority(e.target.value as typeof maintPriority)}>
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="EMERGENCY">Emergency</option>
                </select>
              </Field>
            </div>
            <button
              className="btn-secondary"
              disabled={busy || maintDescription.length < 5}
              onClick={() =>
                act(
                  () => api.post(`/api/v1/housing/tenancies/${id}/maintenance-requests`, { description: maintDescription, priority: maintPriority }),
                  'Maintenance request logged (dispatched to M20 once live — see build report).',
                )
              }
            >
              Log maintenance request
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
